import { useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/feature/TopBar";
import Footer from "@/components/feature/Footer";
import LpForm from "../components/LpForm";
import { PHONE, departments } from "@/mocks/updiag";
import { SHOW_REVIEWS } from "@/config/features";

const faqItems = [
  { q: "Intervenez-vous dans toute l'Île-de-France ?", a: "Oui, Up Diag intervient dans les 8 départements d'Île-de-France : Paris (75), Hauts-de-Seine (92), Seine-Saint-Denis (93), Val-de-Marne (94), Yvelines (78), Essonne (91), Seine-et-Marne (77) et Val-d'Oise (95). Délai d'intervention : 48h maximum." },
  { q: "Le tarif est-il le même partout en IDF ?", a: "Oui, nos tarifs sont identiques dans toute l'Île-de-France. Le déplacement est inclus dans le prix, que vous soyez à Paris, Versailles, Meaux ou Cergy." },
  { q: "Combien coûte un DPE en Île-de-France ?", a: "Le DPE coûte dès 130 € TTC pour un studio ou T1, 150 € pour un T2/T3 et 180 € pour un T4+ ou maison. Ces tarifs sont fixes et incluent le déplacement dans toute l'IDF." },
  { q: "Quelle est la durée de validité d'un DPE ?", a: "Le DPE a une validité de 10 ans depuis la réforme de juillet 2021. Les DPE réalisés avant 2018 ne sont plus valides depuis le 1er janvier 2023." },
  { q: "Puis-je obtenir un RDV le jour même ?", a: "En cas d'urgence, contactez-nous directement par téléphone. Nous faisons notre maximum pour intervenir le jour même selon nos disponibilités." },
];

function FaqItemLP({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`bg-white rounded-2xl border transition-all overflow-hidden ${open ? "border-[#F97316]" : "border-gray-100"}`}>
      <button className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer" onClick={() => setOpen(!open)}>
        <span className={`font-semibold text-sm pr-4 ${open ? "text-[#F97316]" : "text-[#0F3B5F]"}`}>{q}</span>
        <i className={`ri-arrow-down-s-line text-xl flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#F97316]" : "text-gray-400"}`}></i>
      </button>
      {open && <div className="px-6 pb-5 text-sm text-gray-600 leading-relaxed border-t border-gray-50 pt-4">{a}</div>}
    </div>
  );
}

export default function LpDpeIleDeFrance() {
  return (
    <>
      <title>DPE Île-de-France — Diagnostic Énergétique 8 Départements IDF | Up Diag</title>
      <meta name="description" content="DPE certifié COFRAC dans toute l'Île-de-France : Paris, 92, 93, 94, 78, 91, 77, 95. Dès 130 €, RDV sous 48h, rapport le jour même. Devis gratuit." />
      <meta name="robots" content="noindex" />
      <TopBar />

      {/* LP Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#0F3B5F] rounded-xl flex items-center justify-center">
              <i className="ri-home-gear-fill text-white text-lg"></i>
            </div>
            <div>
              <div className="font-bold text-lg text-[#0F3B5F] leading-none" style={{ fontFamily: "Barlow, sans-serif" }}>Up Diag</div>
              <div className="text-[9px] text-gray-400 leading-none">Diagnostic immobilier certifié</div>
            </div>
          </Link>
          <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center gap-2 px-5 py-2.5 bg-[#F97316] text-white rounded-xl font-bold text-sm hover:bg-orange-600 transition-all whitespace-nowrap">
            <i className="ri-phone-fill"></i> {PHONE}
          </a>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative min-h-[600px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/img/readdy/lp2-hero.jpg"
              alt="DPE Île-de-France"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F3B5F]/92 via-[#0F3B5F]/75 to-[#0F3B5F]/30"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-6 py-16 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white text-xs font-bold px-4 py-2 rounded-full mb-6">
                  <i className="ri-map-pin-2-fill text-[#F97316]"></i>
                  8 départements — Paris &amp; toute l'IDF
                </div>
                <h1 className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5" style={{ fontFamily: "Barlow, sans-serif" }}>
                  DPE Île-de-France<br />
                  <span className="text-[#F97316]">Certifié COFRAC<br />Dès 130 € TTC</span>
                </h1>
                <p className="text-lg text-white/85 mb-8 max-w-lg">
                  Votre expert en Diagnostic de Performance Énergétique dans les 8 départements d'Île-de-France. Intervention sous 48h, rapport le jour même.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {[
                    { icon: "ri-award-fill", text: "Certifié COFRAC", color: "text-[#F97316]" },
                    { icon: "ri-map-pin-2-fill", text: "8 départements IDF", color: "text-[#16A34A]" },
                    { icon: "ri-timer-flash-fill", text: "RDV sous 48h", color: "text-[#F59E0B]" },
                    { icon: "ri-price-tag-3-fill", text: "Dès 130 € TTC", color: "text-white" },
                  ].map((pt) => (
                    <div key={pt.text} className="flex items-center gap-3 bg-white/10 border border-white/20 rounded-xl px-4 py-3">
                      <i className={`${pt.icon} ${pt.color} text-lg`}></i>
                      <span className="text-white text-sm font-medium">{pt.text}</span>
                    </div>
                  ))}
                </div>
                {SHOW_REVIEWS && (
                  <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl px-5 py-3">
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F59E0B] text-sm"></i>)}
                    </div>
                    <span className="text-white font-bold text-sm">4.9/5</span>
                    <span className="text-white/60 text-sm">— 127 avis Google vérifiés</span>
                  </div>
                )}
              </div>
              <div className="flex justify-center lg:justify-end">
                <div className="w-full max-w-md">
                  <LpForm
                    preselectedDiag="dpe"
                    formTitle="Devis DPE IDF gratuit"
                    formSubtitle="Réponse sous 2h — Intervention sous 48h"
                    ctaLabel="Obtenir mon devis DPE gratuit"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust bar */}
        <section className="bg-white border-b border-gray-100 py-8">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {[
                { icon: "ri-award-fill", label: "Certifié COFRAC", color: "text-[#F97316]" },
                { icon: "ri-shield-check-fill", label: "RC Pro assurée", color: "text-[#16A34A]" },
                { icon: "ri-map-pin-2-fill", label: "8 départements IDF", color: "text-[#0F3B5F]" },
                { icon: "ri-file-check-line", label: "Rapport le jour même", color: "text-[#0F3B5F]" },
                ...(SHOW_REVIEWS ? [{ icon: "ri-star-fill", label: "4.9/5 Google", color: "text-[#F59E0B]" }] : []),
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2 text-sm font-semibold text-gray-600">
                  <i className={`${item.icon} ${item.color} text-lg`}></i>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Départements */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
                Zones d'intervention — DPE en IDF
              </h2>
              <p className="text-gray-500">Intervention sous 48h dans les 8 départements d'Île-de-France</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {departments.map((dept) => (
                <div key={dept.code} className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-orange-200 hover:-translate-y-1 transition-all duration-300 text-center">
                  <div className="text-4xl font-extrabold text-[#0F3B5F]/20 mb-2" style={{ fontFamily: "Barlow, sans-serif" }}>{dept.code}</div>
                  <h3 className="font-bold text-[#0F3B5F] text-sm mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>{dept.name}</h3>
                  <div className="flex flex-col gap-1">
                    {dept.villes.slice(0, 3).map((v) => (
                      <span key={v} className="text-xs text-gray-400">{v}</span>
                    ))}
                  </div>
                  <div className="mt-3 text-xs font-semibold text-[#F97316]">RDV sous 48h</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Processus */}
        <section className="bg-white py-20">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-extrabold text-[#0F3B5F] mb-3" style={{ fontFamily: "Barlow, sans-serif" }}>
                Comment obtenir votre DPE en IDF ?
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
              {[
                { num: "01", icon: "ri-file-list-3-line", title: "Devis en ligne", desc: "Remplissez le formulaire en 2 min. Devis immédiat." },
                { num: "02", icon: "ri-calendar-check-line", title: "RDV sous 48h", desc: "Choisissez votre créneau. Intervention dans toute l'IDF." },
                { num: "03", icon: "ri-search-eye-line", title: "Intervention", desc: "Notre diagnostiqueur certifié se déplace chez vous." },
                { num: "04", icon: "ri-file-check-line", title: "Rapport le jour même", desc: "Rapport certifié envoyé par email le jour même." },
              ].map((step) => (
                <div key={step.num} className="text-center group">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-[#F97316] bg-white mx-auto mb-4 group-hover:bg-[#F97316] transition-all duration-300">
                    <span className="text-xl font-extrabold text-[#F97316] group-hover:hidden" style={{ fontFamily: "Barlow, sans-serif" }}>{step.num}</span>
                    <i className={`${step.icon} text-xl text-white hidden group-hover:block`}></i>
                  </div>
                  <h3 className="font-bold text-[#0F3B5F] mb-2 text-sm" style={{ fontFamily: "Barlow, sans-serif" }}>{step.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-[#F8FAFC] py-20">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl font-extrabold text-[#0F3B5F] mb-8 text-center" style={{ fontFamily: "Barlow, sans-serif" }}>
              Questions fréquentes — DPE IDF
            </h2>
            <div className="flex flex-col gap-3">
              {faqItems.map((item, i) => <FaqItemLP key={i} q={item.q} a={item.a} />)}
            </div>
          </div>
        </section>

        {/* CTA final */}
        <section className="bg-[#0F3B5F] py-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4" style={{ fontFamily: "Barlow, sans-serif" }}>
              Besoin d'un DPE en Île-de-France ?
            </h2>
            <p className="text-white/75 mb-8">Devis gratuit — Intervention sous 48h — Rapport le jour même</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer">
                <i className="ri-file-list-3-line"></i> Obtenir mon devis gratuit
              </button>
              <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-bold hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-phone-fill"></i> {PHONE}
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
